import { consola } from 'consola'
import type { AnalyticsEventPayload } from './analyticsEvents'
import { getAnalyticsDeviceId } from './analyticsSession'

const logger = consola.withTag('analytics:queue')

const STORAGE_KEY = 'analytics_queue'

/** Отправляем пачкой: на воронку товара приходится десяток событий за минуту. */
const BATCH_SIZE = 20
const FLUSH_INTERVAL_MS = 5000

/**
 * Потолок буфера. Если бэк недоступен долго, старые события выбрасываем — переполненный
 * localStorage сломает корзину и авторизацию, а они важнее аналитики.
 */
const MAX_BUFFERED = 200

let buffer: AnalyticsEventPayload[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let endpoint = ''
let isFlushing = false

const persist = (): void => {
  try {
    if (buffer.length === 0) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer))
  } catch {
    // Хранилище недоступно — работаем только в памяти.
  }
}

const restore = (): void => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) buffer = parsed.slice(-MAX_BUFFERED)
  } catch {
    // Битые данные прошлой версии — начинаем с чистого буфера.
  }
}

/**
 * События, уехавшие в буфер до того, как асинхронный отпечаток устройства досчитался,
 * доклеиваются актуальным значением на отправке — иначе склейка «гость → вошёл» рвётся
 * на первых секундах визита.
 */
const withDeviceId = (batch: AnalyticsEventPayload[]): AnalyticsEventPayload[] => {
  const deviceId = getAnalyticsDeviceId()
  if (!deviceId) return batch
  return batch.map(event => (event.deviceId ? event : { ...event, deviceId }))
}

/**
 * Отправка «на выходе» (вкладка скрывается, приложение уходит в фон).
 * sendBeacon переживает выгрузку страницы, обычный fetch — нет.
 */
const sendViaBeacon = (batch: AnalyticsEventPayload[]): boolean => {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false

  try {
    // text/plain, а не application/json: beacon не умеет preflight, а JSON-Blob —
    // не «simple request», и кросс-доменная прощальная пачка молча терялась бы.
    // Бэк разбирает text/plain на этом маршруте как JSON.
    const blob = new Blob([JSON.stringify({ events: withDeviceId(batch) })], {
      type: 'text/plain;charset=UTF-8',
    })
    return navigator.sendBeacon(endpoint, blob)
  } catch {
    return false
  }
}

const sendViaFetch = async (batch: AnalyticsEventPayload[]): Promise<void> => {
  await $fetch(endpoint, {
    method: 'POST',
    body: { events: withDeviceId(batch) },
    // keepalive позволяет запросу дожить до конца, даже если пользователь ушёл со страницы.
    keepalive: true,
    // Аналитика не должна тащить куки и не должна триггерить refresh токена:
    // событие с истёкшим токеном лучше потерять, чем сломать сессию пользователя.
    credentials: 'omit',
  })
}

/**
 * Пачку с такой ошибкой сервер не примет никогда — возвращать её в буфер значит
 * зациклить ретраи и заткнуть очередь навсегда. 408/429 — временные, их ретраим.
 */
const isPermanentRejection = (error: unknown): boolean => {
  const status = (error as { status?: number; statusCode?: number })?.status
    ?? (error as { statusCode?: number })?.statusCode
  return typeof status === 'number' && status >= 400 && status < 500
    && status !== 408 && status !== 429
}

/**
 * Выгружает буфер. Ошибки не пробрасываются: аналитика не имеет права ломать сценарий.
 * При неудаче события возвращаются в начало буфера и уедут со следующей пачкой —
 * дубли на бэке отсекаются по eventId.
 */
export const flushAnalyticsQueue = async (options: { beacon?: boolean } = {}): Promise<void> => {
  if (!endpoint || buffer.length === 0 || isFlushing) return

  if (options.beacon) {
    // Страница выгружается: второго шанса не будет, сливаем весь буфер попачечно,
    // пока браузер принимает beacon'ы.
    while (buffer.length > 0) {
      const batch = buffer.slice(0, BATCH_SIZE)
      if (!sendViaBeacon(batch)) break
      buffer = buffer.slice(batch.length)
    }
    persist()
    if (buffer.length === 0) return
    // beacon не прошёл (старый WebView) — остаток уйдёт обычным путём ниже.
  }

  const batch = buffer.slice(0, BATCH_SIZE)
  buffer = buffer.slice(batch.length)
  persist()

  isFlushing = true
  try {
    await sendViaFetch(batch)
  } catch (error) {
    if (isPermanentRejection(error)) {
      logger.warn(`Сервер отверг пачку из ${batch.length} событий — выбрасываем`, error)
    } else {
      buffer = [...batch, ...buffer].slice(-MAX_BUFFERED)
      persist()
      logger.debug(`Не удалось отправить ${batch.length} событий, вернули в буфер`, error)
    }
  } finally {
    isFlushing = false
    // Хвост длиннее пачки не должен ждать следующего события — перевзводим таймер сами.
    if (buffer.length > 0) scheduleFlush()
  }
}

const scheduleFlush = (): void => {
  if (timer) return
  timer = setTimeout(() => {
    timer = null
    void flushAnalyticsQueue()
  }, FLUSH_INTERVAL_MS)
}

export const enqueueAnalyticsEvent = (event: AnalyticsEventPayload): void => {
  // Приёмник не поднят (не инициализирована очередь) — копить события в localStorage
  // бессмысленно: они протухнут раньше, чем появится куда их отдать.
  if (!endpoint) return

  buffer.push(event)
  if (buffer.length > MAX_BUFFERED) buffer = buffer.slice(-MAX_BUFFERED)
  persist()

  if (buffer.length >= BATCH_SIZE) void flushAnalyticsQueue()
  else scheduleFlush()
}

/**
 * Поднимает очередь: восстанавливает недоотправленное с прошлого запуска и вешает
 * выгрузку на уход со страницы. Вызывается один раз из плагина.
 */
export const initAnalyticsQueue = (transportEndpoint: string): void => {
  endpoint = transportEndpoint
  restore()

  // pagehide вместо beforeunload: в iOS WebView beforeunload не срабатывает.
  window.addEventListener('pagehide', () => void flushAnalyticsQueue({ beacon: true }))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushAnalyticsQueue({ beacon: true })
  })

  if (buffer.length > 0) void flushAnalyticsQueue()
}
